import { useState, useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { FloatField, FloatWrapper } from "@/components/ui/FormField";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import {
  Plus, Trash2, Edit, ChevronUp, ChevronDown, Activity, Play, Square,
  CheckCircle2, XCircle, Clock, AlertTriangle, Zap, Save, X, Filter, Workflow,
  UserCheck, UserCog, Hourglass, ShieldQuestion,
} from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { WorkflowEditorDialog } from "@/pages/workflows/WorkflowEditor";
import { CardGridSkeleton, ListSkeleton } from "@/components/PageSkeleton";
import {
  type WorkflowDefinition, type WorkflowDefinitionDetail, type WorkflowStep,
  type WorkflowInstance, type WorkflowInstanceDetail, type WorkflowTrigger,
  type TriggerCondition, type TriggerConditionRule, type TriggerConditionField,
  type TriggerOptions,
  apiGetWorkflowDefinitions, apiGetWorkflowDefinition, apiCreateWorkflowDefinition,
  apiUpdateWorkflowDefinition, apiDeleteWorkflowDefinition,
  apiAddWorkflowStep, apiUpdateWorkflowStep, apiDeleteWorkflowStep, apiReorderWorkflowSteps,
  apiGetWorkflowInstances, apiGetWorkflowInstance, apiCancelWorkflowInstance,
  apiClaimWorkflowOwner, apiSetWorkflowOwner, apiGetHrOwnerCandidates,
  apiGetWorkflowTriggers, apiGetWorkflowTriggerOptions, apiGetDocumentTemplates, getTenantSlug,
  apiGetEmployees,
} from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const OPERATORS_STRING = [
  { value: "eq",           label: "equals" },
  { value: "neq",          label: "not equals" },
  { value: "contains",     label: "contains" },
  { value: "not_contains", label: "does not contain" },
  { value: "starts_with",  label: "starts with" },
  { value: "ends_with",    label: "ends with" },
  { value: "in",           label: "is one of (comma-separated)" },
  { value: "not_in",       label: "is not one of (comma-separated)" },
  { value: "regex",        label: "matches regex" },
  { value: "is_empty",     label: "is empty" },
  { value: "is_not_empty", label: "is not empty" },
] as const;

const OPERATORS_NUMBER = [
  { value: "eq",            label: "equals" },
  { value: "neq",           label: "not equals" },
  { value: "gt",            label: "greater than" },
  { value: "gte",           label: "greater than or equal" },
  { value: "lt",            label: "less than" },
  { value: "lte",           label: "less than or equal" },
  { value: "between",       label: "between (min,max)" },
  { value: "is_empty",      label: "is empty" },
  { value: "is_not_empty",  label: "is not empty" },
] as const;

const statusColors: Record<string, string> = {
  active: "bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20",
  completed: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20",
  cancelled: "bg-gray-500/10 text-gray-600 dark:text-gray-400 border-gray-500/20",
  failed: "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20",
};

const stepStatusIcons: Record<string, React.ReactNode> = {
  pending: <Clock className="w-4 h-4 text-muted-foreground" />,
  in_progress: <Play className="w-4 h-4 text-amber-500 dark:text-amber-400" />,
  completed: <CheckCircle2 className="w-4 h-4 text-green-500 dark:text-green-400" />,
  skipped: <XCircle className="w-4 h-4 text-muted-foreground" />,
  failed: <AlertTriangle className="w-4 h-4 text-red-500 dark:text-red-400" />,
};

export default function Workflows() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const qc = useQueryClient();
  const tenantSlug = getTenantSlug();

  const { user } = useAuth();
  const isHrOrAdmin = user?.role?.mainRole === "hr" || user?.role?.mainRole === "admin";

  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") === "instances" ? "instances" : "definitions";
  const [tab, setTab] = useState(initialTab);
  const [editDefId, setEditDefId] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [viewInstanceId, setViewInstanceId] = useState<number | null>(null);
  const [instanceFilter, setInstanceFilter] = useState<string>("");
  // Owner picker — set to a target instance id to open the picker dialog.
  const [ownerPickerInstanceId, setOwnerPickerInstanceId] = useState<number | null>(null);

  // ── Data ──────────────────────────────────────────────────
  const { data: definitions = [], isLoading } = useQuery<WorkflowDefinition[]>({
    queryKey: ["workflow-definitions", tenantSlug],
    queryFn: apiGetWorkflowDefinitions,
  });

  const { data: triggers = [] } = useQuery<WorkflowTrigger[]>({
    queryKey: ["workflow-triggers", tenantSlug],
    queryFn: apiGetWorkflowTriggers,
  });

  const { data: triggerOptions } = useQuery<TriggerOptions>({
    queryKey: ["workflow-trigger-options", tenantSlug],
    queryFn: apiGetWorkflowTriggerOptions,
    staleTime: 5 * 60 * 1000,
  });

  const { data: instances = [], isLoading: isLoadingInstances } = useQuery<WorkflowInstance[]>({
    queryKey: ["workflow-instances", tenantSlug, instanceFilter],
    queryFn: () => apiGetWorkflowInstances(instanceFilter || undefined),
  });

  const invalidate = useCallback(() => {
    qc.invalidateQueries({ queryKey: ["workflow-definitions"] });
    qc.invalidateQueries({ queryKey: ["workflow-instances"] });
    qc.invalidateQueries({ queryKey: ["workflow-instance"] });
  }, [qc]);

  // Count instances awaiting an HR owner — drives the top-of-tab callout.
  const pendingOwnerCount = useMemo(
    () => (instances || []).filter((i) => i.status === "active" && !i.hr_owner_user_id && !!i.owner_pending_since).length,
    [instances],
  );

  // Deep-link from a "pick owner" notification: ?tab=instances&instanceId=N&claim=1
  // → switch to the instances tab, open the detail dialog, and pop the picker.
  useEffect(() => {
    const instanceIdParam = searchParams.get("instanceId");
    if (instanceIdParam) {
      const id = Number(instanceIdParam);
      if (Number.isFinite(id) && id > 0) {
        setTab("instances");
        setViewInstanceId(id);
        if (searchParams.get("claim") === "1") {
          setOwnerPickerInstanceId(id);
        }
        // Strip the URL params so a refresh doesn't re-open the dialog.
        const next = new URLSearchParams(searchParams);
        next.delete("instanceId");
        next.delete("claim");
        setSearchParams(next, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ════════════════════════════════════════════════════════════
  // DEFINITIONS TAB
  // ════════════════════════════════════════════════════════════

  const handleToggleActive = async (def: WorkflowDefinition) => {
    try {
      await apiUpdateWorkflowDefinition(def.id, { is_active: !def.is_active });
      invalidate();
      toast({ title: def.is_active ? t("workflows.deactivated") : t("workflows.activated") });
    } catch (err: any) {
      toast({ title: t("workflows.error"), description: err.message, variant: "destructive" });
    }
  };

  const handleDeleteDef = async (id: number) => {
    try {
      await apiDeleteWorkflowDefinition(id);
      invalidate();
      toast({ title: t("workflows.deleted") });
    } catch (err: any) {
      toast({ title: t("workflows.error"), description: err.message, variant: "destructive" });
    }
  };

  return (
    <AppLayout>
      <div className="w-full px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-violet-500/10 flex items-center justify-center shrink-0">
              <Activity className="h-5 w-5 text-violet-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{t("workflows.title")}</h1>
              <p className="text-sm text-muted-foreground mt-1">{t("workflows.subtitle")}</p>
            </div>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full justify-start bg-transparent h-auto p-0 border-b border-border/60 rounded-none gap-0 overflow-x-auto overflow-y-hidden">
            <TabsTrigger value="definitions" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              <Zap className="h-3.5 w-3.5" />
              {t("workflows.definitions")}
            </TabsTrigger>
            <TabsTrigger value="instances" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              <Activity className="h-3.5 w-3.5" />
              {t("workflows.instances")}
            </TabsTrigger>
          </TabsList>

          {/* ── Definitions Tab ──────────────────────────── */}
          <TabsContent value="definitions" className="mt-6">
            <div className="flex justify-end mb-4">
              <Button onClick={() => setShowCreate(true)} size="sm">
                <Plus className="w-4 h-4 mr-1" /> {t("workflows.newWorkflow")}
              </Button>
            </div>

            {isLoading ? (
              <CardGridSkeleton count={6} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" />
            ) : definitions.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Activity className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
                  <p className="text-muted-foreground">{t("workflows.noWorkflows")}</p>
                  <Button onClick={() => setShowCreate(true)} variant="outline" size="sm" className="mt-4">
                    {t("workflows.createFirst")}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {definitions.map((def) => (
                  <Card key={def.id} className="hover:border-border/80 transition-colors">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-base text-foreground">{def.name}</CardTitle>
                        <Switch
                          checked={def.is_active}
                          onCheckedChange={() => handleToggleActive(def)}
                        />
                      </div>
                      <div className="flex items-center gap-2 flex-wrap mt-1">
                        <Badge variant="outline" className="w-fit text-xs">
                          <Zap className="w-3 h-3 mr-1" />
                          {t(`pilot.trigger.${def.trigger_event}`, triggers.find((trigger) => trigger.event === def.trigger_event)?.label || def.trigger_event)}
                        </Badge>
                        {def.trigger_condition && (def.trigger_condition.rules?.length ?? 0) > 0 && (
                          <Badge variant="outline" className="w-fit text-xs bg-amber-500/10 border-amber-500/20 text-amber-700 dark:text-amber-400">
                            <Filter className="w-3 h-3 mr-1" />
                            {def.trigger_condition.rules.length} condition{def.trigger_condition.rules.length !== 1 ? "s" : ""}
                          </Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      {def.description && (
                        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{def.description}</p>
                      )}
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>
                          {Number(def.active_instances || 0)}{" "}
                          {Number(def.active_instances || 0) !== 1 ? t("workflows.activeInstancesPlural") : t("workflows.activeInstances")}
                        </span>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost" size="sm" className="h-7 px-2"
                            onClick={() => setEditDefId(def.id)}
                            title={t("workflows.openCanvas", "Open canvas editor")}
                          >
                            <Workflow className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300" onClick={() => handleDeleteDef(def.id)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ── Instances Tab ────────────────────────────── */}
          <TabsContent value="instances" className="mt-6">
            <div className="flex gap-2 mb-4">
              {["", "active", "completed", "failed", "cancelled"].map((s) => (
                <Button
                  key={s}
                  variant={instanceFilter === s ? "default" : "outline"}
                  size="sm"
                  onClick={() => setInstanceFilter(s)}
                >
                  {s || t("workflows.all")}
                </Button>
              ))}
            </div>

            {/* Pending-owner callout — only shown to HR/admin when work is waiting */}
            {isHrOrAdmin && pendingOwnerCount > 0 && (
              <Card className="mb-3 border-amber-500/40 bg-amber-500/5">
                <CardContent className="py-3 px-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-amber-500/15 flex items-center justify-center">
                      <ShieldQuestion className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                    </div>
                    <p className="text-sm text-foreground">
                      {pendingOwnerCount === 1
                        ? t("workflows.owner.calloutSingular")
                        : t("workflows.owner.calloutPlural", { count: pendingOwnerCount })}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {isLoadingInstances ? (
              <div className="space-y-2">
                <ListSkeleton rows={5} />
              </div>
            ) : instances.length === 0 ? (
              <p className="text-muted-foreground text-center py-12">{t("workflows.noInstances")}</p>
            ) : (
              <div className="space-y-2">
                {instances.map((inst) => {
                  const isPendingOwner = inst.status === "active"
                    && !inst.hr_owner_user_id
                    && !!inst.owner_pending_since;
                  return (
                  <Card
                    key={inst.id}
                    className={`cursor-pointer hover:border-border/80 transition-colors ${isPendingOwner ? "border-amber-500/40" : ""}`}
                    onClick={() => setViewInstanceId(inst.id)}
                  >
                    <CardContent className="py-3 px-4 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <Badge className={statusColors[inst.status] || ""}>
                          {t(`pilot.status.${inst.status}`, inst.status)}
                        </Badge>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{inst.workflow_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {t("workflows.by")} {inst.initiated_by_name || "System"} &middot; {new Date(inst.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        {/* Owner indicator */}
                        {isPendingOwner ? (
                          <Badge variant="outline" className="border-amber-500/50 text-amber-700 dark:text-amber-300 gap-1">
                            <Hourglass className="w-3 h-3" />
                            {t("workflows.owner.pending")}
                          </Badge>
                        ) : inst.hr_owner_user_id ? (
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <UserCheck className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                            <span className="text-foreground/80">{inst.hr_owner_name ?? `User #${inst.hr_owner_user_id}`}</span>
                            {inst.owner_auto_assigned && (
                              <Badge variant="outline" className="text-[10px] h-4 px-1">{t("workflows.owner.autoAssigned")}</Badge>
                            )}
                          </div>
                        ) : null}

                        {/* Claim / change owner actions */}
                        {isHrOrAdmin && inst.status === "active" && (
                          isPendingOwner ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={(e) => { e.stopPropagation(); setOwnerPickerInstanceId(inst.id); }}
                            >
                              {t("workflows.owner.claim")}
                            </Button>
                          ) : inst.hr_owner_user_id ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 text-xs"
                              onClick={(e) => { e.stopPropagation(); setOwnerPickerInstanceId(inst.id); }}
                              title={t("workflows.owner.switch")}
                            >
                              <UserCog className="w-3.5 h-3.5" />
                            </Button>
                          ) : null
                        )}

                        <div className="text-right text-xs text-muted-foreground">
                          {inst.current_step_name && (
                            <p>{t("workflows.current")}: <span className="text-foreground/70">{inst.current_step_name}</span></p>
                          )}
                          <p>{inst.entity_type}#{inst.entity_id}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );})}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* ── Create Dialog ──────────────────────────────── */}
        <CreateWorkflowDialog
          open={showCreate}
          onClose={() => setShowCreate(false)}
          triggers={triggers}
          triggerOptions={triggerOptions}
          onCreated={(def) => {
            invalidate();
            setShowCreate(false);
            setEditDefId(def.id);
          }}
        />

        {/* ── n8n-style Canvas Editor (replaces legacy WorkflowBuilderDialog) ── */}
        <WorkflowEditorDialog
          defId={editDefId}
          open={editDefId != null}
          onClose={() => { setEditDefId(null); invalidate(); }}
        />

        {/* ── Instance Detail Dialog ─────────────────────── */}
        {viewInstanceId && (
          <InstanceDetailDialog
            instanceId={viewInstanceId}
            onClose={() => { setViewInstanceId(null); invalidate(); }}
            onOpenOwnerPicker={(id) => setOwnerPickerInstanceId(id)}
          />
        )}

        {/* ── HR Owner Picker Dialog ─────────────────────── */}
        {ownerPickerInstanceId && (
          <OwnerPickerDialog
            instanceId={ownerPickerInstanceId}
            onClose={() => { setOwnerPickerInstanceId(null); invalidate(); }}
          />
        )}
      </div>
    </AppLayout>
  );
}

// ════════════════════════════════════════════════════════════════════
// CONDITION VALUE INPUT — smart: dropdown if optionsSource, else text
// ════════════════════════════════════════════════════════════════════

function ConditionValueInput({
  fieldDef,
  value,
  onChange,
  triggerOptions,
}: {
  fieldDef: TriggerConditionField | undefined;
  value: string;
  onChange: (v: string) => void;
  triggerOptions?: TriggerOptions;
}) {
  const source = fieldDef?.optionsSource;
  const opts = source && triggerOptions ? triggerOptions[source] : undefined;

  if (opts && opts.length > 0) {
    return (
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-7 w-36 text-xs"><SelectValue placeholder="select…" /></SelectTrigger>
        <SelectContent>
          {opts.map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <Input
      type={fieldDef?.type === "number" ? "number" : "text"}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="value"
      className="h-7 w-24 text-xs"
    />
  );
}

// ════════════════════════════════════════════════════════════════════
// CREATE WORKFLOW DIALOG
// ════════════════════════════════════════════════════════════════════

function CreateWorkflowDialog({
  open, onClose, triggers, triggerOptions, onCreated,
}: {
  open: boolean;
  onClose: () => void;
  triggers: WorkflowTrigger[];
  triggerOptions?: TriggerOptions;
  onCreated: (def: WorkflowDefinition) => void;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [triggerEvent, setTriggerEvent] = useState("");
  const [condition, setCondition] = useState<TriggerCondition>({ combinator: "AND", rules: [] });
  const [saving, setSaving] = useState(false);

  const currentTrigger = triggers.find((tr) => tr.event === triggerEvent);
  const conditionFields: TriggerConditionField[] = currentTrigger?.conditionFields ?? [];

  const handleTriggerChange = (val: string) => {
    setTriggerEvent(val);
    // Reset conditions when trigger changes — fields may differ
    setCondition({ combinator: "AND", rules: [] });
  };

  const addRule = () => {
    const first = conditionFields[0];
    setCondition((prev) => ({
      ...prev,
      rules: [...prev.rules, { field: first?.field ?? "", operator: first?.type === "number" ? "gt" : "eq", value: "" }],
    }));
  };

  const updateRule = (idx: number, patch: Partial<TriggerConditionRule>) => {
    setCondition((prev) => ({ ...prev, rules: prev.rules.map((r, i) => i === idx ? { ...r, ...patch } : r) }));
  };

  const removeRule = (idx: number) => {
    setCondition((prev) => ({ ...prev, rules: prev.rules.filter((_, i) => i !== idx) }));
  };

  const handleCreate = async () => {
    if (!name.trim() || !triggerEvent) return;
    setSaving(true);
    try {
      const condToSave = condition.rules.length > 0 ? condition : null;
      const def = await apiCreateWorkflowDefinition({
        name,
        description,
        trigger_event: triggerEvent,
        trigger_condition: condToSave,
      });
      toast({
        title: t("workflows.created"),
        description: "Workflow saved as INACTIVE — toggle the Active switch in the workflow builder to start it firing.",
        duration: 8000,
      });
      setName(""); setDescription(""); setTriggerEvent("");
      setCondition({ combinator: "AND", rules: [] });
      onCreated(def);
    } catch (err: any) {
      toast({ title: t("workflows.error"), description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl w-full">
        <DialogHeader>
          <DialogTitle>{t("workflows.createTitle")}</DialogTitle>
          <DialogDescription>{t("workflows.createDescription")}</DialogDescription>
        </DialogHeader>

        {/* ── Two-column layout ── */}
        <div className="grid grid-cols-2 gap-6 pt-1">

          {/* LEFT — Workflow basics */}
          <div className="space-y-4">
            <FloatField
              label={t("workflows.name")}
              required
              value={name}
              onChange={setName}
              placeholder={t("workflows.namePlaceholder")}
            />
            <FloatField
              label={t("workflows.description")}
              type="textarea"
              value={description}
              onChange={setDescription}
              placeholder={t("workflows.descriptionPlaceholder")}
            />
            <FloatWrapper label={t("workflows.triggerEvent")} required>
              <Select value={triggerEvent} onValueChange={handleTriggerChange}>
                <SelectTrigger className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4">
                  <SelectValue placeholder={t("workflows.selectTrigger")} />
                </SelectTrigger>
                <SelectContent>
                  {triggers.map((tr) => (
                    <SelectItem key={tr.event} value={tr.event}>{t(`pilot.trigger.${tr.event}`, tr.label)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FloatWrapper>
            {currentTrigger && (
              <p className="text-[11px] text-muted-foreground mt-1.5 px-1">
                {t("pilot.triggerDescription", { event: t(`pilot.trigger.${currentTrigger.event}`, currentTrigger.label) })}
              </p>
            )}
          </div>

          {/* RIGHT — Trigger conditions */}
          <div className="border border-border rounded-lg p-4 flex flex-col gap-3 bg-muted/20">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold">{t("pilot.canvas.Trigger Conditions")}</span>
              <span className="text-[11px] text-muted-foreground ml-auto">optional</span>
            </div>

            {!triggerEvent ? (
              <p className="text-xs text-muted-foreground italic flex-1 flex items-center justify-center text-center py-4">
                Select a trigger event first to configure conditions.
              </p>
            ) : conditionFields.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">No condition fields available for this trigger.</p>
            ) : (
              <>
                <p className="text-xs text-muted-foreground -mt-1">
                  Only start this workflow when conditions are met. Leave empty to always trigger.
                </p>

                {/* Combinator — only shown when there are 2+ rules */}
                {condition.rules.length > 1 && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground whitespace-nowrap">Match:</span>
                    <Select
                      value={condition.combinator}
                      onValueChange={(v) => setCondition((prev) => ({ ...prev, combinator: v as "AND" | "OR" }))}
                    >
                      <SelectTrigger className="h-7 w-28 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="AND">ALL rules</SelectItem>
                        <SelectItem value="OR">ANY rule</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Rules list */}
                <div className="space-y-2 flex-1">
                  {condition.rules.length === 0 && (
                    <p className="text-xs text-muted-foreground/60 italic text-center py-2">No conditions — workflow always triggers.</p>
                  )}
                  {condition.rules.map((rule, idx) => {
                    const fieldDef = conditionFields.find((f) => f.field === rule.field) ?? conditionFields[0];
                    const operators = fieldDef?.type === "number" ? OPERATORS_NUMBER : OPERATORS_STRING;
                    return (
                      <div key={idx} className="flex items-center gap-1.5 flex-wrap">
                        {/* Field */}
                        <Select
                          value={rule.field}
                          onValueChange={(v) => {
                            const nf = conditionFields.find((f) => f.field === v);
                            updateRule(idx, { field: v, operator: nf?.type === "number" ? "gt" : "eq" });
                          }}
                        >
                          <SelectTrigger className="h-7 w-32 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {conditionFields.map((f) => (
                              <SelectItem key={f.field} value={f.field}>{f.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        {/* Operator */}
                        <Select value={rule.operator} onValueChange={(v) => updateRule(idx, { operator: v })}>
                          <SelectTrigger className="h-7 w-36 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {operators.map((op) => (
                              <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        {/* Value — smart: dropdown if optionsSource, else free text */}
                        <ConditionValueInput
                          fieldDef={fieldDef}
                          value={rule.value}
                          onChange={(v) => updateRule(idx, { value: v })}
                          triggerOptions={triggerOptions}
                        />

                        <button
                          onClick={() => removeRule(idx)}
                          className="h-6 w-6 flex items-center justify-center rounded text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors shrink-0"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>

                <Button variant="outline" size="sm" className="h-7 text-xs w-fit" onClick={addRule}>
                  <Plus className="w-3 h-3 mr-1" /> {t("pilot.canvas.Add Condition")}
                </Button>
              </>
            )}
          </div>
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={onClose}>{t("workflows.cancel")}</Button>
          <Button onClick={handleCreate} disabled={saving || !name.trim() || !triggerEvent}>
            {saving ? t("workflows.creating") : t("workflows.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ════════════════════════════════════════════════════════════════════
// INSTANCE DETAIL DIALOG
// ════════════════════════════════════════════════════════════════════

function InstanceDetailDialog({
  instanceId, onClose, onOpenOwnerPicker,
}: {
  instanceId: number;
  onClose: () => void;
  onOpenOwnerPicker?: (id: number) => void;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const qc = useQueryClient();
  const isHrOrAdmin = user?.role?.mainRole === "hr" || user?.role?.mainRole === "admin";

  const { data: inst, refetch } = useQuery<WorkflowInstanceDetail>({
    queryKey: ["workflow-instance", instanceId],
    queryFn: () => apiGetWorkflowInstance(instanceId),
    // While pending owner, refresh periodically so the countdown stays fresh
    // and we pick up an owner being claimed by another HR user in real time.
    refetchInterval: (q) => {
      const d = q.state.data as WorkflowInstanceDetail | undefined;
      return d && d.status === "active" && !d.hr_owner_user_id && d.owner_pending_since ? 30_000 : false;
    },
  });

  // Live countdown to 72h auto-assignment.
  const [now, setNow] = useState<number>(Date.now());
  useEffect(() => {
    if (!inst?.owner_pending_since) return;
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [inst?.owner_pending_since]);

  if (!inst) return null;

  const isPendingOwner = inst.status === "active"
    && !inst.hr_owner_user_id
    && !!inst.owner_pending_since;

  let countdownLabel: string | null = null;
  if (isPendingOwner && inst.owner_pending_since) {
    const deadline = new Date(inst.owner_pending_since).getTime() + 72 * 60 * 60 * 1000;
    const remainingMs = Math.max(0, deadline - now);
    const totalMinutes = Math.floor(remainingMs / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    countdownLabel = t("workflows.owner.countdown", { hours, minutes });
  }

  const handleCancel = async () => {
    try {
      await apiCancelWorkflowInstance(instanceId);
      toast({ title: t("workflows.cancelled") });
      onClose();
    } catch (err: any) {
      toast({ title: t("workflows.error"), description: err.message, variant: "destructive" });
    }
  };

  const handleClaim = async () => {
    try {
      const r = await apiClaimWorkflowOwner(instanceId);
      if (r.advanceWarning) {
        // Owner was saved but the workflow couldn't start step 1.
        toast({
          title: t("workflows.owner.claimedToast"),
          description: `Step 1 did not start: ${r.advanceWarning}`,
          variant: "destructive",
        });
      } else {
        toast({ title: t("workflows.owner.claimedToast") });
      }
      await refetch();
      qc.invalidateQueries({ queryKey: ["workflow-instances"] });
    } catch (err: any) {
      toast({ title: t("workflows.error"), description: err.message, variant: "destructive" });
    }
  };

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{inst.workflow_name}</DialogTitle>
          <DialogDescription>
            Instance #{inst.id} &middot; {inst.entity_type}#{inst.entity_id}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 mb-4">
          <Badge className={statusColors[inst.status] || ""}>{t(`pilot.status.${inst.status}`, inst.status)}</Badge>
          <span className="text-xs text-muted-foreground">
            {t("workflows.started")} {new Date(inst.created_at).toLocaleString()}
            {inst.completed_at && ` · ${t("workflows.ended")} ${new Date(inst.completed_at).toLocaleString()}`}
          </span>
        </div>

        {/* ── HR owner header ──────────────────────────────────────── */}
        {(isPendingOwner || inst.hr_owner_user_id) && (
          <div className={`mb-4 rounded-lg border px-3 py-3 ${
            isPendingOwner ? "border-amber-500/40 bg-amber-500/5" : "border-emerald-500/30 bg-emerald-500/5"
          }`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2 min-w-0">
                {isPendingOwner ? (
                  <Hourglass className="w-4 h-4 mt-0.5 text-amber-600 dark:text-amber-400 shrink-0 animate-pulse" />
                ) : (
                  <UserCheck className="w-4 h-4 mt-0.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                )}
                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                    {t("workflows.owner.label")}
                  </p>
                  {isPendingOwner ? (
                    <>
                      <p className="text-sm text-foreground mt-0.5">{t("workflows.owner.pending")}</p>
                      {countdownLabel && (
                        <p className="text-xs text-muted-foreground mt-0.5">{countdownLabel}</p>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-foreground mt-0.5 flex items-center gap-2">
                      <span>{inst.hr_owner_name ?? `User #${inst.hr_owner_user_id}`}</span>
                      {inst.owner_auto_assigned && (
                        <Badge variant="outline" className="text-[10px] h-4 px-1">{t("workflows.owner.autoAssigned")}</Badge>
                      )}
                    </p>
                  )}
                </div>
              </div>
              {isHrOrAdmin && inst.status === "active" && (
                <div className="flex flex-col gap-1.5 shrink-0">
                  {isPendingOwner && (
                    <Button size="sm" onClick={handleClaim}>
                      {t("workflows.owner.claim")}
                    </Button>
                  )}
                  {onOpenOwnerPicker && (
                    <Button
                      size="sm"
                      variant={isPendingOwner ? "ghost" : "outline"}
                      onClick={() => onOpenOwnerPicker(inst.id)}
                    >
                      {isPendingOwner ? t("workflows.owner.assignSomeoneElse") : t("workflows.owner.switch")}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <ScrollArea className="max-h-[400px]">
          <div className="space-y-2">
            {(inst.executions || []).map((exec) => (
              <div key={exec.id} className="flex items-start gap-3 py-2 border-b border-border/50 last:border-0">
                <div className="mt-0.5">{stepStatusIcons[exec.status] || stepStatusIcons.pending}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">{exec.step_name}</span>
                    <Badge variant="outline" className="text-[10px] h-5">{t(`pilot.status.${exec.status}`, exec.status)}</Badge>
                  </div>
                  {exec.outcome && (
                    <p className="text-xs text-muted-foreground mt-0.5">{t("workflows.outcome")}: {t(`pilot.status.${exec.outcome}`, exec.outcome)}</p>
                  )}
                  {exec.acted_by_name && (
                    <p className="text-xs text-muted-foreground">{t("workflows.by")}: {exec.acted_by_name}</p>
                  )}
                  {exec.notes && (
                    <p className="text-xs text-muted-foreground italic">"{exec.notes}"</p>
                  )}
                  {exec.completed_at && (
                    <p className="text-[10px] text-muted-foreground/70 mt-0.5">{new Date(exec.completed_at).toLocaleString()}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>

        <DialogFooter>
          {inst.status === "active" && (
            <Button variant="destructive" size="sm" onClick={handleCancel}>
              <Square className="w-3.5 h-3.5 mr-1" /> {t("workflows.cancelWorkflow")}
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>{t("workflows.close")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ════════════════════════════════════════════════════════════════════
// OWNER PICKER DIALOG — lists eligible HR/admin users with their
// current active-instance ownership load; lets one be assigned to a
// workflow instance. Reused by:
//   • Instances tab "Claim" / "Change owner" buttons
//   • InstanceDetailDialog "Switch HR owner" button
// ════════════════════════════════════════════════════════════════════

function OwnerPickerDialog({
  instanceId, onClose,
}: {
  instanceId: number;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const qc = useQueryClient();

  const { data: candidates = [], isLoading } = useQuery({
    queryKey: ["workflow-hr-owner-candidates"],
    queryFn: apiGetHrOwnerCandidates,
  });
  const { data: inst } = useQuery<WorkflowInstanceDetail>({
    queryKey: ["workflow-instance", instanceId],
    queryFn: () => apiGetWorkflowInstance(instanceId),
  });

  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (inst?.hr_owner_user_id) setSelectedUserId(Number(inst.hr_owner_user_id));
  }, [inst?.hr_owner_user_id]);

  const handleConfirm = async () => {
    if (!selectedUserId) return;
    setSaving(true);
    try {
      // If picking yourself AND no owner yet → use claim (faster, idempotent).
      const myUserId = user?.id ? Number(user.id) : null;
      const noOwnerYet = !inst?.hr_owner_user_id;
      let warning: string | null = null;
      if (noOwnerYet && myUserId === selectedUserId) {
        const r = await apiClaimWorkflowOwner(instanceId);
        warning = r.advanceWarning;
      } else {
        await apiSetWorkflowOwner(instanceId, selectedUserId);
      }
      qc.invalidateQueries({ queryKey: ["workflow-instance", instanceId] });
      qc.invalidateQueries({ queryKey: ["workflow-instances"] });
      qc.invalidateQueries({ queryKey: ["workflow-hr-owner-candidates"] });
      if (warning) {
        toast({
          title: t("workflows.owner.savedToast"),
          description: `Step 1 did not start: ${warning}`,
          variant: "destructive",
        });
      } else {
        toast({ title: t("workflows.owner.savedToast") });
      }
      onClose();
    } catch (err: any) {
      toast({ title: t("workflows.error"), description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("workflows.owner.pickerTitle")}</DialogTitle>
          <DialogDescription>{t("workflows.owner.pickerHint")}</DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <p className="text-sm text-muted-foreground py-6 text-center">{t("workflows.loading")}</p>
        ) : candidates.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">{t("workflows.owner.noCandidates")}</p>
        ) : (
          <ScrollArea className="max-h-[360px] -mx-2">
            <div className="px-2 space-y-1">
              {candidates.map((c) => {
                const isSelected = selectedUserId === c.id;
                const isCurrent = inst?.hr_owner_user_id === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setSelectedUserId(c.id)}
                    className={`w-full text-left rounded-md border px-3 py-2 transition-colors ${
                      isSelected
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-border/80 hover:bg-muted/40"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {c.name}
                          {isCurrent && (
                            <Badge variant="outline" className="ml-2 text-[10px] h-4 px-1">
                              {t("workflows.owner.current")}
                            </Badge>
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {c.email} · {c.main_role.toUpperCase()}
                        </p>
                      </div>
                      <Badge variant="outline" className="text-[11px] shrink-0">
                        {t("workflows.owner.activeCount", { count: c.active_owned_count })}
                      </Badge>
                    </div>
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>{t("workflows.close")}</Button>
          <Button onClick={handleConfirm} disabled={!selectedUserId || saving}>
            {saving ? t("workflows.saving") : t("workflows.owner.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
